import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import TextField, { FilledTextFieldProps } from '@mui/material/TextField';
import { ChangeEvent, ForwardedRef, ReactNode, forwardRef } from 'react';
import { useField } from 'formik';

import * as FormTextFieldElements from './FormTextField.elements';
import AlertBox from '../AlertBox';
import FormikError from '../FormikError';
import { addFoundTracking } from '../../utils/tracking';

export interface FormTextFieldProps extends Omit<FilledTextFieldProps, 'variant'> {
  fieldId: string;
  alert?: { title: string; body: ReactNode };
  value?: any;
}

function FormTextField(
  { id, fieldId, alert, value, InputProps, ...props }: FormTextFieldProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const [{ value: formikValue }, { touched, error }, { setValue, setTouched }] = useField(fieldId);

  const hasValidationError = touched && !!error;

  const handleChange = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => setValue(event.target.value);

  const handleBlur = (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setTouched(true);
    addFoundTracking(
      'type',
      JSON.stringify({
        id: fieldId,
        value: event.target.value,
      })
    );
  };

  return (
    <FormTextFieldElements.Wrapper>
      <TextField
        id={id ?? fieldId}
        fullWidth
        value={value ?? formikValue}
        onChange={handleChange}
        onBlur={handleBlur}
        error={hasValidationError}
        InputProps={{ disableUnderline: true, ...InputProps }}
        inputRef={ref}
        {...props}
      />

      <FormikError errorName={fieldId} />

      {!!alert && (
        <Box>
          <AlertBox severity="info">
            <AlertTitle>{alert.title}</AlertTitle>
            {alert.body}
          </AlertBox>
        </Box>
      )}
    </FormTextFieldElements.Wrapper>
  );
}

const FormTextFieldRef = forwardRef<HTMLDivElement, FormTextFieldProps>(FormTextField);

export default FormTextFieldRef;
